import React, { useCallback, useEffect, useState } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import CustomTooltip from "../../../CustomTooltip";
import { FloorIcon } from "../../../../assets/icons";
import { getCassette, getRaccolta } from "../../../../api";
import { useNavigate } from "react-router-dom";
import { getReactSelectStyles } from "../../../../helpers/reactSelectThemeStyles";

const reactSelectStyles = getReactSelectStyles();

const PAGE_SIZE = 10;

function buildRaccoltaOptionLabel(cassettaId, raccoltaNome) {
  return `${cassettaId} — ${raccoltaNome}`;
}

function cassetteResultsToOptions(results = []) {
  const options = [];
  for (const cassetta of results) {
    const raccolte = Array.isArray(cassetta.raccolte) ? cassetta.raccolte : [];
    for (const raccolta of raccolte) {
      const raccoltaNome = raccolta.nome || raccolta.id;
      options.push({
        value: raccolta.id,
        label: buildRaccoltaOptionLabel(cassetta.id, raccoltaNome),
        cassettaId: cassetta.id,
        raccoltaId: raccolta.id,
        raccoltaNome,
      });
    }
  }
  return options;
}

function formatRaccoltaOption(option) {
  return (
    <div className="cassetta-raccolta-option">
      <span className="cassetta-raccolta-option__cassetta">{option.cassettaId}</span>
      <span className="cassetta-raccolta-option__separator">·</span>
      <span className="cassetta-raccolta-option__raccolta">
        {option.raccoltaNome || option.raccoltaId}
      </span>
    </div>
  );
}

const CassettaCollocazione = ({
  disabled,
  repertoData,
  handleCassetaNavigate,
  handleFieldChange,
}) => {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState(null);

  const loadOptions = useCallback(async (search, _prevOptions, { page = 1 } = {}) => {
    const query = search?.trim();
    if (!query) {
      return {
        options: [],
        hasMore: false,
        additional: { page: 1 },
      };
    }

    try {
      const data = await getCassette([
        { key: "page", value: page },
        { key: "pageSize", value: PAGE_SIZE },
        { key: "id", value: query },
      ]);

      return {
        options: cassetteResultsToOptions(data?.results),
        hasMore: page < (data?.totalPages ?? 1),
        additional: { page: page + 1 },
      };
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/");
      } else {
        console.error('Error fetching "cassette"', error);
      }
      return {
        options: [],
        hasMore: false,
        additional: { page: 1 },
      };
    }
  }, [navigate]);

  const handleSelectChange = (option) => {
    setSelectedOption(option);
    if (!option) {
      handleFieldChange("raccolta", null);
      return;
    }
    handleFieldChange("raccolta", {
      raccoltaId: option.raccoltaId,
      cassettaId: option.cassettaId,
    });
    handleFieldChange("esposizione", null);
  };

  useEffect(() => {
    const raccolta = repertoData?.raccolta;
    if (!raccolta?.raccoltaId || raccolta.raccoltaId === "?") {
      setSelectedOption(null);
      return;
    }

    let cancelled = false;

    const hydrateSelection = async () => {
      try {
        const data = await getRaccolta(raccolta.raccoltaId);
        if (cancelled || !data) return;
        setSelectedOption({
          value: data.id,
          label: buildRaccoltaOptionLabel(
            data.cassettaId,
            data.nome || data.id
          ),
          cassettaId: data.cassettaId,
          raccoltaId: data.id,
          raccoltaNome: data.nome,
        });
      } catch {
        if (cancelled) return;
        setSelectedOption({
          value: raccolta.raccoltaId,
          label: buildRaccoltaOptionLabel(
            raccolta.cassettaId,
            raccolta.raccoltaId
          ),
          cassettaId: raccolta.cassettaId,
          raccoltaId: raccolta.raccoltaId,
        });
      }
    };

    hydrateSelection();
    return () => {
      cancelled = true;
    };
  }, [repertoData?.raccolta?.raccoltaId, repertoData?.raccolta?.cassettaId]);

  return (
    <div className="cassetta-collocazione-container">
      <div className="input-wrapper">
        <label className="input-label" htmlFor="cassetta-raccolta-select">
          Cassetta / scomparto
        </label>
        <div className="cassetta-collocazione-select-row">
          <AsyncPaginate
            inputId="cassetta-raccolta-select"
            className="cassetta-collocazione-select"
            classNamePrefix="cassetta-collocazione-select"
            value={selectedOption}
            loadOptions={loadOptions}
            onChange={handleSelectChange}
            additional={{ page: 1 }}
            placeholder="Digita l'ID cassetta per cercare..."
            isDisabled={disabled}
            isClearable
            defaultOptions={false}
            debounceTimeout={300}
            formatOptionLabel={formatRaccoltaOption}
            noOptionsMessage={({ inputValue }) =>
              inputValue?.trim()
                ? "Nessuna cassetta o scomparto trovato"
                : "Digita l'ID cassetta"
            }
            styles={reactSelectStyles}
          />
          <CustomTooltip text="Visualizza cassetta">
            <button
              type="button"
              className="cassetta-collocazione-nav-btn"
              onClick={handleCassetaNavigate}
              disabled={!repertoData?.raccolta?.cassettaId}
            >
              <FloorIcon
                fillColor="var(--archeo-color)"
                width={24}
                height={24}
              />
            </button>
          </CustomTooltip>
        </div>
      </div>
    </div>
  );
};

export default CassettaCollocazione;
